import React, { useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  ApiButton,
  Button,
  ContentTemplate,
  Divider,
  ErrorInfo,
  Grid,
  HeaderTemplate,
  KeyValue,
  Progress,
  SectionHeader,
  TitleHeader,
  Typography,
} from 'web-library';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { candidateService } from '../../../api';
import ExamInstruction from './ExamInstruction';
import { useExamNavigation } from '../../../routes/ExamRoute';
import ExamDraftApplication from './ExamDraftApplication';
import Acknowledgement from '../../../components/Acknowledgement';
import ExamNotAvailable from './ExamNotAvailable';
import {
  ExamRegistrationEligibilityResponse,
  ExamRegistrationType,
} from '../../../api/candidate/exam/model';
import ProceedSupplementaryModal from './ProceedSupplementaryModal';
import { usePostTransactionLog } from '../../../api-refactored/candidate/usePostTransactionLog';
import { parseLocationSearch } from '../../../utils/StringUtils';

const useStyles = makeStyles()((theme) =>
({
  button: {
    paddingTop: theme.spacing(2),
  },
  errorText: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(3),
  },
}));

const useCallApis = (
  examType: string,
  token: string | null,
  t: TFunction,
  setProceedSupplementaryModal: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const history = useHistory();
  const [eligibleErrorCode, setEligibleErrorCode] = useState<string | undefined>();
  const [examTitle, setExamTitle] = useState<string>(t('exam->>RegisterExam.RegisterForAnExam'));
  const [isSupplementary, setIsSupplementary] = useState<boolean>(false);
  const [isDeclared, setIsDeclared] = useState<boolean>(true);
  const [acknowledgementAlert, setAcknowledgementAlert] = useState<boolean>(false);
  const { toSelectSubject, toRegisterExamInstruction } = useExamNavigation();
  const [isOtherExamDraftExist, setIsOtherExamDraftExist] = useState<boolean>(false);
  const [requiredSupplementaryTransition, setRequiredSupplementaryTransition] = useState<boolean>(
    false
  );
  const [examTypeDisplay, setExamTypeDisplay] = useState<string>('-');
  const [applicationId, setApplicationId] = useState<string>('');
  const [iexamRegistration, setHasIexamRegistration] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const { state: locationState } = useLocation<{ waiveLateFee: boolean } | undefined>();
  const waiveLateFee = !!locationState && locationState.waiveLateFee;

  const {
    isSuccess: eligibleSuccess,
    isError: eligibleError,
    data: examRegistration,
  } = candidateService.exam.candidate.useGetPendingExamRegistration(
    { examType: examType || '', token },
    {
      onSuccess: (res) => {
        if (res.currentExamRegistrationType !== ExamRegistrationType.EXAM) {
          setExamTitle(t('exam->>SupplementaryRegistration.RegisterForAnExamSupplementary'));
          setIsDeclared(waiveLateFee || false);
          setIsSupplementary(true);
          setApplicationId(res.draft?.applicationId || '');
        }
        if (res.draft?.examType && examType !== res.draft?.examType) {
          setIsOtherExamDraftExist(true);
        } else {
          setRequiredSupplementaryTransition(
            res.draft?.examRegistrationType === ExamRegistrationType.EXAM &&
            res.currentExamRegistrationType !== ExamRegistrationType.EXAM
          );
        }
      },
      onError: (err) => {
        setEligibleErrorCode(err.response?.data.code || '');
      },
      onSettled: () => {
        if (examType && examTypesData) {
          examTypesData?.forEach((item) => {
            if (item.code === examType) {
              setExamTypeDisplay(item.description);
            }
          });
        }
      },
    }
  );

  const {
    isSuccess: examTypeSuccess,
    data: examTypesData,
  } = candidateService.exam.candidate.useGetExamTypes();

  const { mutate: apiMutation, status: postStatus } = usePostTransactionLog();
  const createExamRegistrationMutation = candidateService.exam.candidate.usePostCreateExamRegistration(
    (res) => {
      history.push(toSelectSubject.replace(':uuid', res.data.uuid), { token });
    },
    (error) => {
      setErrorMessage(error.response.data.message);
    }
  );

  const checkAcknowledgement = (callback: () => void) => {
    if (!isDeclared) {
      setAcknowledgementAlert(true);
    } else {
      setAcknowledgementAlert(false);
      callback();
    }
  };

  const handleStartRegistration = () => {
    setHasIexamRegistration(examRegistration?.hasIexamRegistration || false);
    checkAcknowledgement(() => {
      if (!examRegistration?.hasIexamRegistration) {
        if (requiredSupplementaryTransition) {
          setProceedSupplementaryModal(true);
        } else {
          createExamRegistrationMutation.mutate({ examType, token });
        }
      }
    });

    if (
      examRegistration?.draft?.applicationId &&
      isDeclared &&
      !examRegistration?.hasIexamRegistration
    ) {
      apiMutation({
        auditActionType: 'CONTINUE_EXAM_REGISTRATION',
        applicationId,
      });
    }
  };

  const handleContinueRegistration = () => {
    history.push(
      toRegisterExamInstruction.replace(':examType', examRegistration?.draft?.examType || '')
    );
  };

  const handleAgreement = () => {
    setAcknowledgementAlert(false);
    setIsDeclared(!isDeclared);
  };

  return {
    allApiSuccess: eligibleSuccess && examTypeSuccess,
    eligibleError,
    eligibleErrorCode,
    errorMessage,
    examRegistration,
    examTypesData,
    examTypeDisplay,
    createExamRegistrationMutation,
    examTitle,
    isSupplementary,
    handleAgreement,
    handleContinueRegistration,
    handleStartRegistration,
    isDeclared,
    acknowledgementAlert,
    isOtherExamDraftExist,
    requiredSupplementaryTransition,
    postStatus,
    waiveLateFee,
    iexamRegistration,
  };
};
const generateButtonLabel = (
  t: TFunction,
  requiredSupplementaryTransition: boolean,
  examRegistration?: ExamRegistrationEligibilityResponse,
  examType?: string
) =>
  examRegistration?.draft?.applicationId &&
    examRegistration?.draft?.examType === examType &&
    !requiredSupplementaryTransition
    ? t('exam->>RegisterExamInstructions.Continue Registration')
    : t('exam->>RegisterExamInstructions.Start a new Registration');

const ExamEligibility = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();
  const { toSelectSubject } = useExamNavigation();

  const [proceedSupplementaryModal, setProceedSupplementaryModal] = useState<boolean>(false);

  // @ts-ignore
  const { examType } = useParams();
  const examTypeCode = examType || '';

  const location = useLocation();

  const { token = null } = parseLocationSearch(location.search);

  const {
    allApiSuccess,
    eligibleError,
    eligibleErrorCode,
    errorMessage,
    examRegistration,
    examTypeDisplay,
    createExamRegistrationMutation,
    examTitle,
    isSupplementary,
    handleAgreement,
    handleStartRegistration,
    handleContinueRegistration,
    isDeclared,
    acknowledgementAlert,
    isOtherExamDraftExist,
    requiredSupplementaryTransition,
    postStatus,
    waiveLateFee,
    iexamRegistration,
  } = useCallApis(examTypeCode, token, t, setProceedSupplementaryModal);

  if (eligibleError && eligibleErrorCode) {
    return (
      <ExamNotAvailable
        examTitle={examTitle}
        examTypeDisplay={examTypeDisplay}
        eligibleErrorCode={eligibleErrorCode}
      />
    );
  }

  if (allApiSuccess) {
    return (
      <>
        <TitleHeader title={examTitle} />
        <HeaderTemplate>
          <KeyValue
            keyText={t('exam->>RegisterExamInstructions.Exam Level')}
            valueText={examTypeDisplay}
          />
        </HeaderTemplate>
        <ContentTemplate type="large">
          <ExamInstruction examType={examTypeCode} />
          <Divider paddingTop />
          <SectionHeader title={t('exam->>RegisterExamInstructions.GetStarted')} />
          {examRegistration?.draft?.applicationId && !requiredSupplementaryTransition && (
            <ExamDraftApplication
              examLevels={examRegistration.draft?.examLevels}
              examYear={examRegistration.draft?.examYear}
              applicationId={examRegistration.draft?.applicationId}
              examType={examRegistration.draft?.examType}
              displayExamType={examType}
            />
          )}
          {isSupplementary && !isOtherExamDraftExist && !waiveLateFee && (
            <Acknowledgement
              isAgreed={isDeclared}
              handleAgreement={handleAgreement}
              showError={acknowledgementAlert}
              content={t('exam->>SupplementaryRegistration.AcknowledgementMessage')}
            />
          )}
          <ErrorInfo label={errorMessage || ''} />
          <Grid container direction="row" alignItems="flex-end">
            <Grid xs={12} md={4} className={classes.button}>
              {iexamRegistration && (
                <Typography className={classes.errorText} variant="body1">
                  {t('components->>IExamsExist.ContactSeab')}
                </Typography>
              )}
              <ApiButton
                fullWidth
                id="button-registerExam-start"
                status={createExamRegistrationMutation.status && postStatus}
                disabled={isOtherExamDraftExist || iexamRegistration}
                onClick={() => {
                  handleStartRegistration();
                }}
              >
                {generateButtonLabel(
                  t,
                  requiredSupplementaryTransition,
                  examRegistration,
                  examType
                )}
              </ApiButton>
            </Grid>
            <Grid xs={12} md={4}>
              {isOtherExamDraftExist && (
                <Button
                  fullWidth
                  id="button-registerExam-continue"
                  color="primary"
                  onClick={() => {
                    handleContinueRegistration();
                  }}
                  variant="text"
                >
                  {t('exam->>RegisterExamInstructions.Continue Registration')}
                </Button>
              )}
            </Grid>
          </Grid>
          <ProceedSupplementaryModal
            modelOpen={proceedSupplementaryModal}
            setModelOpen={setProceedSupplementaryModal}
            callbackMethod={(uuid) => {
              history.push(toSelectSubject.replace(':uuid', uuid));
            }}
            draftUuid={examRegistration?.draft?.applicationUuid}
            examType={examType}
          />
        </ContentTemplate>
      </>
    );
  }

  // waiting api result
  return (
    <ContentTemplate transparent continuous>
      <Progress color="secondary" />
    </ContentTemplate>
  );
};
export default ExamEligibility;
